import { forwardRef } from 'react'

import { cn } from '@/lib/utils/cn'

export interface SwitchProps {
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  className?: string
  'aria-label'?: string
}

/**
 * 토글 스위치. 코드베이스에 체크박스류 프리미티브가 없어 새로 만든다(`Input.tsx`는 텍스트 입력뿐).
 *
 * 네이티브 `<input type="checkbox">` 대신 `<button role="switch" aria-checked>` 를 쓴다 —
 * 스크린리더가 "온/오프"가 아니라 "켜짐/꺼짐" 스위치 시맨틱으로 읽고, 스페이스·엔터 모두
 * 버튼 기본 동작으로 토글된다(`Button.tsx`와 동일한 `forwardRef` 패턴).
 */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { checked, onChange, disabled, className, ...aria },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => {
        onChange(!checked)
      }}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-brand-500' : 'bg-slateish-300',
        className
      )}
      {...aria}
    >
      <span
        aria-hidden
        className={cn(
          'inline-block h-5 w-5 translate-x-0.5 rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-[22px]' : 'translate-x-0.5'
        )}
      />
    </button>
  )
})
